import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUiLang } from '../utils/lang';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SEO from '../components/SEO';
import { ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LegalPage = () => {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetch(`${API_URL}/api/legal/${slug}`)
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (cancelled) return;
        if (!ok) {
          setError(d.detail || 'Page not found');
          setPage(null);
        } else {
          setPage(d);
        }
      })
      .catch(() => !cancelled && setError('Network error'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [slug]);

  const lang = getUiLang(i18n);
  const tr = page?.translations?.[lang] || page?.translations?.sk || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid={`legal-page-${slug}`}>
      <SEO
        title={tr.title || slug}
        description={(tr.content || '').slice(0, 200)}
        url={`/legal/${slug}`}
      />
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-orange-600 hover:text-orange-700 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Späť na hlavnú
        </Link>

        {loading && (
          <div className="flex justify-center py-16">
            <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full" />
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-700">
            {error}
          </div>
        )}

        {page && !loading && (
          <article className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-10">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              {tr.title || slug}
            </h1>
            <div className="prose prose-orange max-w-none legal-prose">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {tr.content || ''}
              </ReactMarkdown>
            </div>
            {page.updated_at && (
              <div className="mt-8 pt-4 border-t text-xs text-gray-400">
                Posledná aktualizácia: {new Date(page.updated_at).toLocaleDateString('sk-SK')}
              </div>
            )}
          </article>
        )}
      </div>
    </div>
  );
};

export default LegalPage;
